import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DATA_SOURCE_REF, isDynamic, DataSourceModeToggle, DynamicDataSourceField } from '../dataSourceBinding';
import { WaField, WaDropdownArrow, optionalLabel } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { DropdownCore } from '../../../../flow-core/components/DropdownCore';

// Auto-generate a stable, unique option id from its human-readable title so users
// never have to author internal ids by hand. Slug the title (fallback "option")
// and append a counter until unique within the list. Generated once at add-time.
const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const generateOptionId = (title, existingIds) => {
  const base = slugify(title) || 'option';
  let candidate = base;
  let n = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  return candidate;
};

export const DropdownComponent = new FlowComponent({
  ...DropdownCore,
  renderPreview(properties) {
    return (
      <WaField
        label={optionalLabel(properties.label || 'Select option', properties.required)}
        helper={isDynamic(properties) ? `⚡ options from ${properties[DATA_SOURCE_REF]}` : undefined}
        trailing={<WaDropdownArrow />}
        properties={properties}
      />
    );
  },
  renderPropertyEditor(properties, onChange, context) {
    var isDynamicFlow = context?.flowType === 'DYNAMIC' || context?.isDynamicFlow;

    var handleArrayItemChange = (index, field, value) => {
      var currentArray = properties['data-source'] || [];
      var newArray = [...currentArray];
      newArray[index] = { ...newArray[index], [field]: value };
      onChange('data-source', newArray);
    };

    var handleAddArrayItem = () => {
      var currentArray = properties['data-source'] || [];
      var title = 'New Option';
      var newItem = { id: generateOptionId(title, currentArray.map((o) => o.id)), title };
      onChange('data-source', [...currentArray, newItem]);
    };

    var handleRemoveArrayItem = (index) => {
      var currentArray = properties['data-source'] || [];
      var newArray = currentArray.filter((_, i) => i !== index);
      onChange('data-source', newArray);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          inputProps={{ maxLength: 80 }}
          helperText={`${(properties.label || '').length}/80 characters`}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Initial Value</InputLabel>
          <Select
            value={properties['init-value'] || ''}
            onChange={(e) => onChange('init-value', e.target.value)}
            label="Initial Value"
          >
            <MenuItem value="">None</MenuItem>
            {(properties['data-source'] || []).map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Validation
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Options
        </Typography>

        {isDynamicFlow && <DataSourceModeToggle properties={properties} onChange={onChange} />}

        {isDynamicFlow && isDynamic(properties) ? (
          <DynamicDataSourceField properties={properties} onChange={onChange} />
        ) : (
          <Box>
            {(properties['data-source'] || []).map((item, index) => (
              <Box
                key={index}
                sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Title"
                    value={item.title || ''}
                    onChange={(e) => handleArrayItemChange(index, 'title', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveArrayItem(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddArrayItem}
              variant="outlined"
            >
              Add Option
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          State
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.enabled !== false}
              onChange={(e) => onChange('enabled', e.target.checked)}
            />
          }
          label="Enabled"
        />

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
